import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Contact } from 'src/app/models/contact.model';
import { Service } from 'src/app/models/service.model';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from 'src/app/shared/utils/dropdown.util';
import {
  InlineErrors,
  updateInlineErrors,
} from 'src/app/shared/utils/form-validation.util';

@Component({
  selector: 'app-ticket-noc',
  templateUrl: './ticket-noc.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    SharedModule,
  ],
})
export class TicketNocComponent {
  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() primaryReporters: Contact[];

  @Input() submissionError: boolean;

  @Input() isSubmitting: boolean;

  @Output() submitNocForm = new EventEmitter();

  @Input() errorMessage: string;

  /** When true, Customer Ticket Number field is hidden (e.g. for eBonding accounts). */
  @Input() hideCustomerTicketNumber: boolean;

  nocFormGroup = new UntypedFormGroup({
    reporter: new UntypedFormControl('', Validators.required),
    locationOfService: new UntypedFormControl(''),
    service: new UntypedFormControl('', Validators.required),
    serviceArea: new UntypedFormControl('', Validators.required),
    preferredContactMethod: new UntypedFormControl('', Validators.required),
    alternateContactMethod: new UntypedFormControl(''),
    alternatePhone: new UntypedFormControl(''),
    description: new UntypedFormControl('', Validators.required),
    identifyIssueBy: new UntypedFormControl('location'),
    isPowerOn: new UntypedFormControl(''),
    canPerformIntrusiveTests: new UntypedFormControl(''),
    testDays: new UntypedFormControl(''),
    testHoursStart: new UntypedFormControl(''),
    testHoursEnd: new UntypedFormControl(''),
    accessHours: new UntypedFormControl('Anytime'),
    siteLocalFirst: new UntypedFormControl(''),
    siteLocalLast: new UntypedFormControl(''),
    siteLocalPhone: new UntypedFormControl(
      '',
      Validators.pattern(/^[+]?[\d\s()\-.]{7,20}$/),
    ),
    siteLocalEmail: new UntypedFormControl('', Validators.email),
    customerTicketNumber: new UntypedFormControl(''),
    countryCode: new UntypedFormControl(''),
  });

  areServicesLoading = signal(true);

  areUsersLoading = signal(true);

  inlineErrors: InlineErrors = {};

  formSubmitAttempted = signal(false);

  isServiceAreaDropdownOpen = signal(false);

  readonly serviceAreaOptions = [
    'My Service Is Down',
    'My Service is Degraded or Intermittent',
    'I would like to make a change to my existing service',
    'I have a question or request for my service',
  ];

  readonly testDayOptions = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  readonly powerVerificationOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  readonly intrusiveTestingOptions = [
    { label: 'Any time', value: 'Yes (Any Time)' },
    { label: 'Custom', value: 'Yes (Custom)' },
    { label: 'No', value: 'No' },
  ];

  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isServiceAreaDropdownOpen,
    );
  }

  get selectedServiceAreaLabel(): string {
    return this.nocFormGroup?.value?.serviceArea ?? '';
  }

  toggleServiceAreaDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isServiceAreaDropdownOpen);
  }

  selectServiceArea(serviceArea: string, event: Event): void {
    stopEvent(event);
    this.nocFormGroup.controls.serviceArea.setValue(serviceArea);
    closeDropdown(this.isServiceAreaDropdownOpen);
    this.changeServiceArea();
  }

  selectPowerVerification(value: string): void {
    this.nocFormGroup.controls.isPowerOn.setValue(value);
    this.validateControl('isPowerOn');
  }

  selectIntrusiveTesting(value: string): void {
    this.nocFormGroup.controls.canPerformIntrusiveTests.setValue(value);
    this.changeIntrusiveTesting();
  }

  isTestDaySelected(day: string): boolean {
    const selectedDays = this.nocFormGroup.controls.testDays.value;
    return Array.isArray(selectedDays) && selectedDays.includes(day);
  }

  toggleTestDay(day: string): void {
    const selectedDays = this.nocFormGroup.controls.testDays.value;
    const nextDays = Array.isArray(selectedDays) ? [...selectedDays] : [];
    const existingIndex = nextDays.indexOf(day);

    if (existingIndex > -1) {
      nextDays.splice(existingIndex, 1);
    } else {
      nextDays.push(day);
    }

    this.nocFormGroup.controls.testDays.setValue(nextDays);
    this.validateControl('testDays');
  }

  private readonly fieldErrors: Record<string, string> = {
    reporter: 'Please select a reporter',
    service: 'Please select a service',
    serviceArea: 'Please select a service area',
    description: 'Please describe your issue',
    siteLocalEmail: 'Please enter your email address.',
    siteLocalPhone: 'Please enter a valid phone number.',
  };

  private updateControlsWithoutShowingErrors(controlNames: string[]): void {
    controlNames.forEach((controlName) => {
      this.nocFormGroup.controls[controlName]?.updateValueAndValidity();
      this.inlineErrors[controlName] = null;
    });
  }

  canSubmit(): boolean {
    return updateInlineErrors(
      this.nocFormGroup,
      this.inlineErrors,
      this.fieldErrors,
    );
  }

  validateControl(controlName: string): boolean {
    const control = this.nocFormGroup.controls[controlName];

    if (!control) {
      return true;
    }

    control.updateValueAndValidity();

    if (control.valid) {
      this.inlineErrors[controlName] = null;
      return true;
    }

    if (this.formSubmitAttempted() || this.inlineErrors[controlName]) {
      if (control.errors?.email) {
        this.inlineErrors[controlName] = 'Please enter a valid email address.';
      } else if (control.errors?.pattern) {
        this.inlineErrors[controlName] =
          this.fieldErrors[controlName] ?? 'Please enter a valid value.';
      } else {
        this.inlineErrors[controlName] =
          this.fieldErrors[controlName] ?? 'Please complete this field.';
      }
    }

    return false;
  }

  changeServiceArea(): void {
    if (
      this.nocFormGroup.value.serviceArea === 'My Service Is Down' ||
      this.nocFormGroup.value.serviceArea ===
        'My Service is Degraded or Intermittent'
    ) {
      this.nocFormGroup.controls.isPowerOn.setValidators(Validators.required);
      this.nocFormGroup.controls.canPerformIntrusiveTests.setValidators(
        Validators.required,
      );
      this.nocFormGroup.controls.accessHours.setValidators(Validators.required);
      this.nocFormGroup.controls.siteLocalFirst.setValidators(
        Validators.required,
      );
      this.nocFormGroup.controls.siteLocalLast.setValidators(
        Validators.required,
      );
      this.nocFormGroup.controls.siteLocalPhone.setValidators([
        Validators.required,
        Validators.pattern(/^[+]?[\d\s()\-.]{7,20}$/),
      ]);
      this.nocFormGroup.controls.siteLocalEmail.setValidators([
        Validators.required,
        Validators.email,
      ]);
    } else {
      this.nocFormGroup.controls.isPowerOn.clearValidators();
      this.nocFormGroup.controls.canPerformIntrusiveTests.clearValidators();
      this.nocFormGroup.controls.accessHours.clearValidators();
      this.nocFormGroup.controls.siteLocalFirst.clearValidators();
      this.nocFormGroup.controls.siteLocalLast.clearValidators();
      this.nocFormGroup.controls.siteLocalPhone.clearValidators();
      this.nocFormGroup.controls.siteLocalEmail.clearValidators();
      this.nocFormGroup.controls.testDays.clearValidators();
      this.nocFormGroup.controls.testHoursStart.clearValidators();
      this.nocFormGroup.controls.testHoursEnd.clearValidators();
    }

    this.nocFormGroup.controls.serviceArea.updateValueAndValidity();
    this.inlineErrors.serviceArea = this.nocFormGroup.controls.serviceArea.valid
      ? null
      : this.inlineErrors.serviceArea;
    this.updateControlsWithoutShowingErrors([
      'isPowerOn',
      'canPerformIntrusiveTests',
      'accessHours',
      'siteLocalFirst',
      'siteLocalLast',
      'siteLocalPhone',
      'siteLocalEmail',
      'testDays',
      'testHoursStart',
      'testHoursEnd',
    ]);
  }

  changeIntrusiveTesting(): void {
    if (this.nocFormGroup.value.canPerformIntrusiveTests === 'Yes (Custom)') {
      this.nocFormGroup.controls.testDays.setValidators(Validators.required);
      this.nocFormGroup.controls.testHoursStart.setValidators(
        Validators.required,
      );
      this.nocFormGroup.controls.testHoursEnd.setValidators(
        Validators.required,
      );
    } else {
      this.nocFormGroup.controls.testDays.clearValidators();
      this.nocFormGroup.controls.testHoursStart.clearValidators();
      this.nocFormGroup.controls.testHoursEnd.clearValidators();
    }
    this.nocFormGroup.controls.canPerformIntrusiveTests.updateValueAndValidity();
    this.inlineErrors.canPerformIntrusiveTests = this.nocFormGroup.controls
      .canPerformIntrusiveTests.valid
      ? null
      : this.inlineErrors.canPerformIntrusiveTests;
    this.updateControlsWithoutShowingErrors([
      'testDays',
      'testHoursStart',
      'testHoursEnd',
    ]);
  }

  submitTicket(): void {
    this.formSubmitAttempted.set(true);
    if (this.canSubmit()) {
      const selectedService = this.services.find(
        (service) => service.Id === this.nocFormGroup.value.service,
      );
      const request = {
        contactId: this.nocFormGroup.value.reporter,
        customerTicketNumber: this.hideCustomerTicketNumber
          ? ''
          : this.nocFormGroup.value.customerTicketNumber,
        portalcustomcontact__c: this.nocFormGroup.value.alternatePhone,
        description: this.nocFormGroup.value.description,
        service__c: selectedService.Id,
        portal_ticket_type__c: this.nocFormGroup.value.serviceArea,
        preferred_contact_method__c:
          this.nocFormGroup.value.preferredContactMethod ||
          this.nocFormGroup.value.alternateContactMethod,
        canPerformIntrusiveTests:
          this.nocFormGroup.value.canPerformIntrusiveTests,
        testDays: this.nocFormGroup.value.testDays,
        testHoursStart: new Date(
          `${new Date().toDateString()} ${this.nocFormGroup.value.testHoursStart}`,
        ),
        testHoursEnd: new Date(
          `${new Date().toDateString()} ${this.nocFormGroup.value.testHoursEnd}`,
        ),
        isPowerOn: this.nocFormGroup.value.isPowerOn,
        accessHours: this.nocFormGroup.value.accessHours,
        siteLocalFirst: this.nocFormGroup.value.siteLocalFirst,
        siteLocalLast: this.nocFormGroup.value.siteLocalLast,
        siteLocalPhone: this.nocFormGroup.value.siteLocalPhone,
        siteLocalEmail: this.nocFormGroup.value.siteLocalEmail,
      };
      this.submitNocForm.emit(request);
    }
  }

  usersLoaded(): void {
    this.areUsersLoading.set(false);
  }

  servicesLoaded(): void {
    this.areServicesLoading.set(false);
  }
}
